import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import type { SwitchOption } from '@/components/switch-mode-picker/switch-mode-picker';
import { SwitchModePicker } from '@/components/switch-mode-picker/switch-mode-picker';
import { ListingStageName } from '@/features/listing-stage/types';
import { Button } from '@/ui/button/button';
import { Inline } from '@/ui/line/line';
import { MinimizedHeader } from '@/ui/typography/across-platform';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import type { ProjectListingsDisplayMode } from '../project-listings/project-listings';

import { useListingStagesToSwitchTabs } from './stage-management-table/use-listing-stages-to-switch-tabs';
import { projectIdState } from '../project.state';
import { useCompanies } from '@/features/companies/hooks/use-companies.query';
import { FilterSearchInput } from '@/components/filters/filter-search-input';
import { useListingsSearchParam } from './use-listings-search-param';
import { useAtomValue, useSetAtom } from 'jotai';

import { modalState } from '@/state/modal.state';

export const ProjectListingsHeader = ({
  displayMode,
  isDisabledMode,
  isLoading,
  toggleDisplayMode,
}: {
  displayMode: ProjectListingsDisplayMode;
  isDisabledMode?: boolean;
  isLoading: boolean;
  toggleDisplayMode: () => void;
}) => {
  const projectId = useAtomValue(projectIdState);
  const { refetch: refreshCompanies } = useCompanies();
  const [isSearchInputActive, setIsSearchInputActive] = useState(false);
  const {
    listingsSearchParam,
    setListingsSearchParam,
    deleteListingsSearchParam,
  } = useListingsSearchParam();
  const { t } = useTranslation('projects');
  const { hasRequiredPermission } = useUserPermissions();
  const setModalState = useSetAtom(modalState);
  const { setActiveTabByStageName } = useListingStagesToSwitchTabs();
  const hasEditPermission =
    hasRequiredPermission(PERMISSION.ADD_COMPANIES_TO_PROJECTS) &&
    Boolean(projectId);

  const handleDeactivateSearchInput = () => {
    setIsSearchInputActive(false);
    deleteListingsSearchParam();
  };

  const handleRemoveSearchInput = () => {
    deleteListingsSearchParam();
  };

  const openAddCompany = () => {
    if (!projectId) return;
    setModalState({
      state: 'addCompany',
      projectId,
      onSuccess: async () => {
        await refreshCompanies();
        return setActiveTabByStageName(ListingStageName.discovery);
      },
    });
  };

  const displayModeConfig: Record<ProjectListingsDisplayMode, SwitchOption> =
    useMemo(
      () => ({
        table: {
          label: t`listings.table`,
          isOn: displayMode === 'table',
          icon: 'Table',
        },
        byStage: {
          label: t`listings.byStage`,
          isOn: displayMode === 'byStage',
          icon: 'ListBullets',
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [displayMode],
    );

  return (
    <Inline data-testid="project-listings-header">
      <Inline justifyContent="flex-start" gap="36px">
        <TableHeader>{t`projectOverview.companiesInProjectHeader`}</TableHeader>
        {!isDisabledMode && (
          <SwitchModePicker
            switchOptions={Object.values(displayModeConfig)}
            toggleMode={toggleDisplayMode}
          />
        )}
      </Inline>

      <Inline justifyContent="end">
        <FilterSearchInput
          onChange={(value: string) => setListingsSearchParam(value)}
          placeholder={t`listings.search`}
          disabled={isLoading}
          onRemove={handleRemoveSearchInput}
          isInputActivated={isSearchInputActive || !!listingsSearchParam}
          onInputActivate={() => setIsSearchInputActive(true)}
          onInputDeactivate={handleDeactivateSearchInput}
          defaultValue={listingsSearchParam ?? ''}
          displayIconOnly
          delay={500}
        />

        {hasEditPermission && (
          <Button onClick={openAddCompany} startIcon="Plus" variant="secondary">
            {t`Add company`}
          </Button>
        )}
      </Inline>
    </Inline>
  );
};

const TableHeader = styled(MinimizedHeader)`
  padding: 10px 0;
`;
